<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import { slide } from 'svelte/transition';
	// Stores
	import PreferenceStore from '@/Stores/PreferenceStore.js';
	import IncomingBetStore from '@/Stores/IncomingBetStore.js';
	import BetCashoutReviewStore from '@/Stores/BetCashoutReviewStore.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import Polling from '@/Components/Polling.svelte';
	import IncomingBetAlert from '@/Components/IncomingBetAlert.svelte';
	// Partials
	import BetCount from '@/Partials/Bets/BetCount.svelte';
	import IncomingBet from '@/Partials/Bets/_IncomingBet.svelte';
	import BetTickerRow from '@/Partials/Bets/_BetTickerRow.svelte';
	// Constants
	import { BetCategory, BetType } from '@/Constants/Bet.js';

	dayjs.extend(localizedFormat);

	$: racingBets = $IncomingBetStore && getRacingBets();
	$: sportsBets = $IncomingBetStore && getSportsBets();
	$: bets = racingBets && sportsBets && filter && $IncomingBetStore && filterBetStore();
	$: cashoutReviewBetCount = $BetCashoutReviewStore.count();

	let height = ['h-incoming-sm', 'h-incoming-md', 'h-incoming-lg', 'h-screen'];
	let heightIndex = $PreferenceStore.app.incomingBetsHeightIndex;
	let filter = $PreferenceStore.app.incomingFilter || [BetCategory.Racing, BetCategory.Sport];

	function nextHeightIndex() {
		heightIndex = heightIndex === height.length - 1 ? 0 : heightIndex + 1;
		PreferenceStore.updateSetting('app.incomingBetsHeightIndex', heightIndex);
	}

	function getIncomingBets() {
		IncomingBetStore.getBets();
	}

	function filterBetStore() {
		return $IncomingBetStore
			.filter((bet) => {
				// keep any bets matching our filter AND include multis
				if (
					![BetType.Multi, BetType.SameRaceMulti, BetType.SameGameMulti].includes(bet.bet_type.id) &&
					filter.indexOf(bet.legs.data.items[0].leg_selections.data.items[0].type.id) >= 0
				) {
					return true;
				}
				if (filter.includes(BetCategory.Racing) && bet.bet_type.id === BetType.SameRaceMulti) {
					return true;
				}
				if (filter.includes(BetCategory.Sport) && bet.bet_type.id === BetType.SameGameMulti) {
					return true;
				}
				if (bet.bet_type.id === BetType.Multi) {
					let multiHasRacing = false;
					let multiHasSport = false;
					for (const betLeg of bet.legs.data.items) {
						if (betLeg.leg_selections.data.items[0].type.id === BetCategory.Racing) {
							multiHasRacing = true;
						}
						if (
							betLeg.leg_selections.data.items[0].type.id === BetCategory.Sport ||
							betLeg.bet_type.id === BetType.SameGameMulti
						) {
							multiHasSport = true;
						}
					}
					if (filter.includes(BetCategory.Racing) && multiHasRacing) {
						return true;
					}
					if (filter.includes(BetCategory.Sport) && multiHasSport) {
						return true;
					}
				}
			})
			.sortBy('created_at');
	}

	function getSportsBets() {
		return $IncomingBetStore.filter((bet) => {
			if (
				![BetType.Multi, BetType.SameRaceMulti, BetType.SameGameMulti].includes(bet.bet_type.id) &&
				[BetCategory.Sport].includes(bet.legs.data.items[0].leg_selections.data.items[0].type.id)
			) {
				return true;
			}
			if (bet.bet_type.id === BetType.SameGameMulti) {
				return true;
			}
			if (bet.bet_type.id === BetType.Multi) {
				let multiHasSport = false;
				for (const betLeg of bet.legs.data.items) {
					if (
						betLeg.leg_selections.data.items[0].type.id === BetCategory.Sport ||
						betLeg.bet_type.id === BetType.SameGameMulti
					) {
						multiHasSport = true;
					}
				}
				if (multiHasSport) {
					return true;
				}
			}
		});
	}
	function getRacingBets() {
		return $IncomingBetStore.filter((bet) => {
			if (
				![BetType.Multi, BetType.SameRaceMulti, BetType.SameGameMulti].includes(bet.bet_type.id) &&
				[BetCategory.Racing].includes(bet.legs.data.items[0].leg_selections.data.items[0].type.id)
			) {
				return true;
			}
			if (bet.bet_type.id === BetType.SameRaceMulti) {
				return true;
			}
			if (bet.bet_type.id === BetType.Multi) {
				let multiHasRacing = false;
				for (const betLeg of bet.legs.data.items) {
					if (betLeg.leg_selections.data.items[0].type.id === BetCategory.Racing) {
						multiHasRacing = true;
					}
				}
				if (multiHasRacing) {
					return true;
				}
			}
		});
	}

	function clickFilter(value) {
		let index = filter.indexOf(value);
		if (index >= 0) {
			// remove
			filter = [...filter.slice(0, index), ...filter.slice(index + 1)];
		} else {
			// add
			filter = [...filter, value];
		}
		PreferenceStore.updateSetting('app.incomingFilter', filter);
	}
</script>

<IncomingBetAlert betCount={bets.count()} />
<Polling pollFunction={getIncomingBets} frequency={30} pollOnStart={true} />
{#if $PreferenceStore.app.showIncomingBets}
	<div transition:slide class="sticky top-0 z-20 mx-2 mt-2 mb-2 bg-white border rounded shadow-md border-brand-black">
		<div class="flex flex-col">
			<div class="flex flex-row justify-between p-2 text-white bg-black font-bold">
				<div class="flex items-center">Incoming bets</div>
				<div class="flex items-center">
					<div class="flex mr-2 space-x-2">
						<button
							title="Racing"
							on:click={() => clickFilter(BetCategory.Racing)}
							class="relative btn {filter.indexOf(BetCategory.Racing) >= 0
								? 'text-white btn-primary-action font-normal'
								: 'text-light-grey border border-light-grey'}"
						>
							<span
								class="absolute top-0 right-0 flex justify-center items-center -mt-1 -mr-1"
								class:hidden={racingBets.count() === 0}
								class:h-4.5={racingBets.count() >= 10}
								class:w-4.5={racingBets.count() >= 10}
								class:h-3.5={racingBets.count() < 10}
								class:w-3.5={racingBets.count() < 10}
							>
								<span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />

								<span
									class="relative inline-flex rounded-full bg-red-500 flex-row justify-center items-center"
									class:h-4.5={racingBets.count() >= 10}
									class:w-4.5={racingBets.count() >= 10}
									class:h-3.5={racingBets.count() < 10}
									class:w-3.5={racingBets.count() < 10}
									><span
										class="text-white text-xs pb-[2px]
">{racingBets.count()}</span
									></span
								>
							</span>
							<FontIcon icon="gallop" />
						</button>
						<button
							title="Sport"
							on:click={() => clickFilter(BetCategory.Sport)}
							class="relative btn {filter.indexOf(BetCategory.Sport) >= 0
								? 'text-white btn-primary-action font-normal'
								: 'text-light-grey border border-light-grey'}"
						>
							<span
								class="absolute top-0 right-0 flex justify-center items-center -mt-1 -mr-1"
								class:hidden={sportsBets.count() === 0}
								class:h-4.5={sportsBets.count() >= 10}
								class:w-4.5={sportsBets.count() >= 10}
								class:h-3.5={sportsBets.count() < 10}
								class:w-3.5={sportsBets.count() < 10}
							>
								<span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />

								<span
									class="relative inline-flex rounded-full bg-red-500 flex-row justify-center items-center"
									class:h-4.5={sportsBets.count() >= 10}
									class:w-4.5={sportsBets.count() >= 10}
									class:h-3.5={sportsBets.count() < 10}
									class:w-3.5={sportsBets.count() < 10}
									><span
										class="text-white text-xs pb-[2px]
">{sportsBets.count()}</span
									></span
								>
							</span>
							<FontIcon icon="basketball" />
						</button>
					</div>
					{#if $PreferenceStore.app.showIncomingBets}
						<button
							on:click={nextHeightIndex}
							class="p-1 mr-3 bg-gray-400 rounded-full text-dark-grey focus:outline-none"
						>
							<Icon name="expand" classes="h-3" />
						</button>
					{/if}
					<BetCount {bets} />
					<button
						on:click={() =>
							PreferenceStore.updateSetting('app.showIncomingBets', !$PreferenceStore.app.showIncomingBets)}
						class="py-1 pl-2 cursor-pointer focus:outline-none text-white"
					>
						<Icon name={$PreferenceStore.app.showIncomingBets ? 'chevron-up' : 'chevron-down'} classes="h-3" />
					</button>
				</div>
			</div>
			<div class="overflow-y-auto {height[heightIndex]}">
				<div class="grid w-full incoming-grid">
					{#each bets.all() as bet (bet.id)}
						<IncomingBet {bet} />
					{/each}
				</div>
			</div>
		</div>

		{#if cashoutReviewBetCount > 0}
			<div class="flex flex-col">
				<div class="flex items-center justify-between p-1.5 text-white bg-black font-bold">Cashout Review</div>
				<div class="overflow-y-auto h-auto">
					<table class="w-full">
						<thead>
							<tr class="text-xs text-left text-gray-500 uppercase">
								<th class="py-1 px-2 font-medium text-center">Ticket</th>
								<th class="py-1 px-2 font-medium text-center">Date</th>
								<th class="py-1 px-2 font-medium text-center">User</th>
								<th class="py-1 px-2 font-medium">Event</th>
								<th class="py-1 px-2 font-medium">Selection</th>
								<th class="py-1 px-2 font-medium">Bet</th>
								<th class="py-1 px-2 font-medium">Cashout Amount</th>
								<th class="py-1 px-2 font-medium text-center" />
							</tr>
						</thead>
						<tbody class="bg-white">
							{#each $BetCashoutReviewStore.all() as bet (bet.id)}
								<tr class="hover:bg-gray-100 focus-within:bg-gray-100">
									<BetTickerRow {bet} betTicker={false} reviewCashout={true} />
								</tr>
							{/each}
						</tbody>
					</table>
				</div>
			</div>
		{/if}
	</div>
{/if}

<style>
	.incoming-grid {
		grid-template-columns: 0.5fr 1.4fr 1.4fr 0.9fr 1.8fr 1.5fr;
	}
</style>
